import React from 'react'
import classnames from 'classnames'

import { buildAriaProps, buildCss, buildDataProps, buildHtmlProps } from '../utilities/props'
import { globalProps } from '../utilities/globalProps'
import { GlobalEventProps, globalEventProps } from '../utilities/globalEventProps'
import DateTime from '../pb_kit/dateTime';
import { resolveTimeZone } from './activeSupportTimeZones'

import Caption from '../pb_caption/_caption'

type TimestampProps = {
  align?: "left" | "center" | "right",
  aria?: { [key: string]: string },
  className?: string | string[],
  dark?: boolean,
  data?: string,
  text: string,
  timestamp: Date | string,
  timezone: string,
  htmlOptions?: {[key: string]: string | number | boolean | (() => void)},
  id?: string,
  showCurrentYear?: boolean,
  showDate?: boolean,
  showUser?: boolean,
  hideUpdated?: boolean,
  showTime?: boolean,
  showTimezone?: boolean,
  unstyled?: boolean,
  variant?: "default" | "elapsed" | "updated"
} & GlobalEventProps

const Timestamp = (props: TimestampProps): React.ReactElement => {
  const {
    align = 'left',
    aria = {},
    className,
    dark = false,
    data = {},
    htmlOptions = {},
    text,
    timezone,
    timestamp,
    showCurrentYear = false,
    showDate = true,
    showUser = false,
    hideUpdated = false,
    showTime = true,
    showTimezone = false,
    unstyled = false,
    variant = 'default',
  } = props

  const ariaProps = buildAriaProps(aria)
  const dataProps = buildDataProps(data)
  const htmlProps = buildHtmlProps(htmlOptions)
  const classes = classnames(
    buildCss('pb_timestamp_kit', align),
    globalProps(props),
    className
  )

  const resolvedTimezone = resolveTimeZone(timezone)
  const currentYear = new Date().getFullYear().toString()
  const shouldShowUser = showUser == true && text.length > 0
  const shouldShowTimezone = showTimezone == true && timezone.length > 0
  const updatedText = hideUpdated ? "" : "Last updated"
  const userDisplay = shouldShowUser ? ` by ${text}` : ''

  let timeDisplay = `${DateTime.toHour(timestamp, resolvedTimezone)}:${DateTime.toMinute(timestamp, resolvedTimezone)}${DateTime.toMeridiem(timestamp, resolvedTimezone)}`

  const fullTimeDisplay = () => {
    if (shouldShowTimezone) {
      timeDisplay = `${timeDisplay} ${DateTime.toTimeZone(timestamp, resolvedTimezone)}`
    }
    return timeDisplay
  }

  const baseDateDisplay = () => {
  let display = `${DateTime.toMonth(timestamp, resolvedTimezone)} ${DateTime.toDay(timestamp, resolvedTimezone)}`
  if (DateTime.toYear(timestamp, resolvedTimezone).toString() !== currentYear || showCurrentYear) {
    display = `${display}, ${DateTime.toYear(timestamp, resolvedTimezone)}`
  }
  return display
}

  const fullDateDisplay = () => {
    const fullDisplay = baseDateDisplay()
    return showTime ? `${fullDisplay} ${' \u00b7 '} ${fullTimeDisplay()}` : fullDisplay
  }

  const formatUpdatedString = () => {
    const finalUpdatedString = []
    if (shouldShowUser) finalUpdatedString.push(`by ${text}`)
    if (showDate && !showTime) finalUpdatedString.push(`on ${baseDateDisplay()}`)
    if (showDate && showTime) finalUpdatedString.push(`on ${baseDateDisplay()} at ${fullTimeDisplay()}`)
    if (showTime && !showDate) finalUpdatedString.push(`at ${fullTimeDisplay()}`)
    return `Last updated ${finalUpdatedString.join(' ')}`
  }

  const formatElapsedString = () => {
    return `${updatedText} ${userDisplay} ${DateTime.fromNow(timestamp)}`
  }

  const timestampText = () => {
    switch (variant) {
    case 'updated':
      return formatUpdatedString()
    case 'elapsed':
      return formatElapsedString()
    default:
      if (showDate && showTime) return timestamp ? fullDateDisplay() : text
      if (showDate && !showTime) return baseDateDisplay()
      if (!showDate && showTime) return fullTimeDisplay()
      return text
    }
  }

  return (
    <div
        {...ariaProps}
        {...dataProps}
        {...htmlProps}
        {...globalEventProps(props)}
        className={classes}
    >
      <div className="pb_timestamp_kit">
        {unstyled ? (
          <div>
            {timestampText()}
          </div>
        ) : (
          <Caption
              dark={dark}
              size="xs"
              text={timestampText()}
          />
        )}
      </div>
    </div>
  )
}

export default Timestamp
